import { debugLog } from "./debug";

export interface EmailContext {
  subject: string;
  body: string;
  from: string;
  /** Graph-compatible (REST v2.0) message id, used to look up the email's
   * sensitivity label (see data-classification.ts). `undefined` when the
   * item has no itemId yet, or REST-id conversion fails — treated as "no
   * label readable" by the classification gate, never a thrown error. */
  id?: string;
  /** Sender's display name (e.g. "PayPal Security"), as distinct from `from`
   * (the actual address). Read from the same `item.from`/`item.sender`
   * object already used for `from` — no extra mailbox round-trip. Used by
   * the phishing-signal heuristic (phishing.ts) to detect a display name
   * that doesn't match the sending domain. "" when unavailable. */
  fromDisplayName?: string;
}

export interface AttachmentMeta {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
}

// v1 extractable-text formats (see plan gap-1 decisions): plain text, Markdown,
// and JSON — decoded from the attachment's base64 file content as UTF-8. PDF and
// other binary/image formats return metadata only (no content) until a text
// extraction library is introduced (out of scope here — see plan YAGNI list).
const TEXT_EXTRACTABLE_EXTENSIONS = [".txt", ".md", ".json", ".csv", ".log"];

function isTextExtractable(name: string): boolean {
  const lower = name.toLowerCase();
  return TEXT_EXTRACTABLE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

// Decodes a base64 string to UTF-8 text. atob() gives a binary string (one
// byte per char); TextDecoder handles multi-byte UTF-8 sequences correctly.
function decodeBase64Text(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
}

export async function readCurrentEmail(): Promise<EmailContext> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");

  const body = await new Promise<string>((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result: Office.AsyncResult<string>) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Failed to read email body: ${result.error?.message ?? "unknown"}`));
        return;
      }
      resolve(result.value ?? "");
    });
  });

  // In READ mode (an opened/selected message) item.subject is a plain string;
  // the Subject object with getAsync() is compose-only (Office docs). Calling
  // getAsync on the read-mode string throws "not a function" — the bug that left
  // getEmailContext returning null and the assistant with no email context.
  const subject =
    typeof item.subject === "string"
      ? item.subject
      : await new Promise<string>((resolve, reject) => {
          (item.subject as Office.Subject).getAsync((result: Office.AsyncResult<string>) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(
                new Error(`Failed to read email subject: ${result.error?.message ?? "unknown"}`)
              );
              return;
            }
            resolve(result.value ?? "");
          });
        });

  const from: string =
    (item.from as { emailAddress?: string } | undefined)?.emailAddress ??
    (item.sender as { emailAddress?: string } | undefined)?.emailAddress ??
    "";

  const fromDisplayName: string =
    (item.from as { displayName?: string } | undefined)?.displayName ??
    (item.sender as { displayName?: string } | undefined)?.displayName ??
    "";

  // itemId is the EWS-formatted id; convert to a REST v2.0 id, which Graph's
  // v1.0 endpoints accept directly. Never let a conversion failure (e.g. an
  // unsaved/malformed id) break reading the email itself.
  let id: string | undefined;
  try {
    id = item.itemId
      ? Office.context.mailbox.convertToRestId(item.itemId, Office.MailboxEnums.RestVersion.v2_0)
      : undefined;
  } catch (error) {
    debugLog(
      "office-mail:rest-id-conversion-failed",
      error instanceof Error ? error.name : "unknown"
    );
    id = undefined;
  }

  return { subject, body, from, id, fromDisplayName };
}

/**
 * Best-effort read of the compose body as HTML — used to snapshot whatever
 * Outlook auto-populated (chiefly the signature, with its original
 * formatting/images/links) before an AI-driven `insertReplyBody` call
 * replaces the user's drafted text, so that boilerplate can be re-appended
 * instead of being wiped out. Resolves "" (never rejects) since this is a
 * non-critical enhancement — a failed snapshot just means nothing gets
 * preserved, not a broken insert.
 */
export async function getComposeBodyHtml(): Promise<string> {
  const item = Office.context.mailbox?.item;
  if (!item) return "";
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Html, (result: Office.AsyncResult<string>) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
    });
  });
}

/**
 * Replaces the compose body with `text`, followed by `preserveTailHtml` if
 * given (normally a `getComposeBodyHtml` snapshot taken before the draft was
 * touched, i.e. the signature). Writes via HTML coercion rather than Text:
 * Text coercion does not reliably preserve line breaks across Outlook
 * clients (Desktop/Web/Mac/new Outlook), and would discard the preserved
 * HTML's own formatting.
 */
export async function insertReplyBody(text: string, preserveTailHtml = ""): Promise<void> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = `<div>${escaped.replace(/\n/g, "<br>")}</div>${preserveTailHtml}`;
  return new Promise((resolve, reject) => {
    item.body.setAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Failed to insert reply: ${result.error?.message ?? "unknown"}`));
          return;
        }
        resolve();
      }
    );
  });
}

export async function applyCategories(categories: string[]): Promise<void> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");
  return new Promise((resolve, reject) => {
    item.categories.addAsync(categories, (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Failed to apply categories: ${result.error?.message ?? "unknown"}`));
        return;
      }
      resolve();
    });
  });
}

export async function getEmailContext(): Promise<EmailContext | null> {
  try {
    return await readCurrentEmail();
  } catch (error) {
    // Returning null is intentional (no item / read failure → no context pill),
    // but never swallow the reason silently: a broken read must be diagnosable
    // (CLAUDE.md A3). Enable with localStorage.aiAssistantDebug = "1".
    debugLog("office-mail:context-read-failed", error instanceof Error ? error.name : "unknown");
    return null;
  }
}

/**
 * Lists attachments on the currently open email (metadata only — no content).
 * Stays within Mail.Read: item.attachments is available on the Office.js read
 * surface without any additional Graph scope.
 */
export async function listAttachments(): Promise<AttachmentMeta[]> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");
  const attachments = (item.attachments ?? []) as Office.AttachmentDetails[];
  return attachments.map((a) => ({
    id: a.id,
    name: a.name,
    contentType: a.contentType,
    size: a.size,
    isInline: a.isInline,
  }));
}

/**
 * Reads the extracted text content of an attachment by ID. v1 supports plain
 * text, Markdown, JSON, CSV, and log files (see {@link TEXT_EXTRACTABLE_EXTENSIONS}).
 * For any other type (PDF, images, binary), returns an empty string alongside
 * the metadata — OCR/PDF text extraction is out of scope for v1.
 */
export async function readAttachmentText(attachmentId: string): Promise<string> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");

  const attachments = (item.attachments ?? []) as Office.AttachmentDetails[];
  const meta = attachments.find((a) => a.id === attachmentId);
  if (!meta) throw new Error(`Attachment not found: ${attachmentId}`);

  if (!isTextExtractable(meta.name)) {
    return JSON.stringify({
      name: meta.name,
      contentType: meta.contentType,
      size: meta.size,
      content: null,
      note: "Binary/unsupported format in v1 — name and type only, no extracted text.",
    });
  }

  const content = await new Promise<Office.AttachmentContent>((resolve, reject) => {
    item.getAttachmentContentAsync(
      attachmentId,
      (result: Office.AsyncResult<Office.AttachmentContent>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Failed to read attachment: ${result.error?.message ?? "unknown"}`));
          return;
        }
        resolve(result.value);
      }
    );
  });

  if (content.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
    return decodeBase64Text(content.content);
  }
  // Non-file formats (url/eml/iCalendar) are not extractable text in v1.
  return "";
}

/**
 * True when a mail item is currently open/selected. Synchronous and cheap — no
 * async body read that can fail or race Office readiness — so the UI can gate
 * "current email" actions on item presence rather than on a full context read
 * (which returns null on any read failure and only re-runs on ItemChanged).
 */
export function hasActiveMailItem(): boolean {
  try {
    return !!Office.context.mailbox?.item;
  } catch {
    return false;
  }
}

/**
 * True when the currently open mail item is a compose surface (new message,
 * reply, or forward being drafted) rather than a received message being read.
 * Reuses the same discriminant already relied on in {@link readCurrentEmail}:
 * READ mode's `item.subject` is a plain string; COMPOSE mode's is a `Subject`
 * object exposing `.getAsync()`.
 */
export function isComposeMode(): boolean {
  const item = Office.context.mailbox?.item;
  if (!item) return false;
  return typeof item.subject !== "string";
}

/**
 * Register a handler fired when the selected/opened mail item changes.
 *
 * ItemChanged requires Mailbox 1.5 and is not available on every surface the
 * add-in activates on (compose, appointments, and the older clients admitted
 * by the 1.3 requirement gate in the manifests). Failure is non-fatal — the
 * task pane simply won't auto-refresh its item context there — so it is
 * swallowed rather than propagated: an unhandled throw would abort the caller's
 * mount effect and leave the cleanup unregistered.
 */
export function addItemChangedHandler(handler: () => void): void {
  try {
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
  } catch (error) {
    debugLog(
      "office-mail:item-changed-register-failed",
      error instanceof Error ? error.name : "unknown"
    );
  }
}

/**
 * Remove all ItemChanged handlers. The Mailbox API does not support removing a
 * single handler; since the add-in registers only one, this is equivalent.
 * Tolerates the same unsupported-surface failure as {@link addItemChangedHandler}.
 */
export function removeItemChangedHandler(): void {
  try {
    Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
  } catch (error) {
    debugLog(
      "office-mail:item-changed-unregister-failed",
      error instanceof Error ? error.name : "unknown"
    );
  }
}
